import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, FileSignature, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui'
import {
  applyContractNameChange,
  fetchContractNameChangePreview,
  type ContractNameChangePreview,
} from '@/services/partyIdentityService'
import {
  fetchPartnerGroupMembership,
  type PartnerGroupMembershipRow,
} from '@/services/partnerGroupMembershipService'

const INPUT_CLASS =
  'mt-1 w-full rounded border border-border bg-background px-2 py-2 text-sm text-foreground outline-none focus:border-primary'

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String((error as { message?: unknown }).message ?? '').trim()
    if (message) return message
  }
  return fallback
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

export function LegalNameChangeModal({
  partnerNumber,
  contractId,
  onClose,
  onDone,
}: {
  partnerNumber: number
  contractId?: number
  onClose: () => void
  onDone?: () => void
}) {
  const [members, setMembers] = useState<PartnerGroupMembershipRow[]>([])
  const [personId, setPersonId] = useState<number | null>(null)
  const [preview, setPreview] = useState<ContractNameChangePreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [newFirstName, setNewFirstName] = useState('')
  const [newLastName, setNewLastName] = useState('')
  const [signedAt, setSignedAt] = useState(todayIso())
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso())
  const [contractChecked, setContractChecked] = useState<Record<number, boolean>>({})

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchPartnerGroupMembership(partnerNumber)
      .then(rows => {
        if (cancelled) return
        const active = rows.filter(row => row.membership_status === 'АКТИВЕН' && !row.end_date)
        setMembers(active)
        setPersonId(active.length === 1 ? active[0].person_id : null)
        setError(null)
      })
      .catch(loadError => {
        if (!cancelled) setError(errorMessage(loadError, 'Не удалось загрузить состав группы'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [partnerNumber])

  useEffect(() => {
    if (personId == null) {
      setPreview(null)
      return
    }
    let cancelled = false
    setPreviewLoading(true)
    fetchContractNameChangePreview(personId)
      .then(data => {
        if (cancelled) return
        setPreview(data)
        setNewFirstName(data.person.first_name)
        setNewLastName(data.person.last_name)
        setContractChecked(Object.fromEntries(data.contracts.map(contract => [
          contract.contract_id,
          contractId != null ? contract.contract_id === contractId : contract.is_active,
        ])))
        setError(null)
      })
      .catch(loadError => {
        if (!cancelled) setError(errorMessage(loadError, 'Не удалось загрузить договоры'))
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [personId, contractId])

  const selectedContracts = useMemo(
    () => preview?.contracts.filter(contract => contractChecked[contract.contract_id]) ?? [],
    [preview, contractChecked]
  )
  const nameChanged = Boolean(preview) && (
    newFirstName.trim() !== preview?.person.first_name ||
    newLastName.trim() !== preview?.person.last_name
  )
  const canSubmit = !saving && !previewLoading && Boolean(
    preview &&
    nameChanged &&
    newFirstName.trim() &&
    newLastName.trim() &&
    signedAt &&
    effectiveFrom &&
    selectedContracts.length > 0
  )

  async function handleSubmit() {
    if (!preview || personId == null || !canSubmit) return
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const result = await applyContractNameChange({
        personId,
        newFirstName,
        newLastName,
        signedAt,
        effectiveFrom,
        contractIds: selectedContracts.map(contract => contract.contract_id),
      })
      setSuccess(
        `Имя изменено: ${result.old_last_name} ${result.old_first_name} → ` +
        `${result.new_last_name} ${result.new_first_name}. ` +
        `ДС создано: ${result.amendment_ids.length}, юрлиц обновлено: ${result.entity_ids.length}.`
      )
      setPreview(previous => previous ? {
        ...previous,
        person: {
          ...previous.person,
          first_name: result.new_first_name,
          last_name: result.new_last_name,
          display_name: null,
        },
      } : previous)
      onDone?.()
    } catch (submitError: unknown) {
      setError(errorMessage(submitError, 'Смена имени не проведена'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-8"
      onClick={onClose}
    >
      <section
        className="my-6 w-full max-w-xl rounded-xl bg-background p-4 shadow-lg"
        onClick={event => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
              <FileSignature size={15} />
              Смена имени по договорам
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              Новое имя попадёт в договорный контур и во все списки.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Закрыть"
          >
            <X size={16} />
          </button>
        </div>

        {error && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <div>{error}</div>
          </div>
        )}
        {success && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
            <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
            <div>{success}</div>
          </div>
        )}

        {loading ? (
          <div className="flex h-28 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 size={16} className="animate-spin" />
            Загрузка
          </div>
        ) : members.length === 0 ? (
          <div className="rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
            У франчайзи №{partnerNumber} нет активных участников.
          </div>
        ) : (
          <div className="space-y-4">
            <label className="block text-xs font-medium text-muted-foreground">
              Текущее имя
              <select
                value={personId ?? ''}
                onChange={event => setPersonId(event.target.value ? Number(event.target.value) : null)}
                className={INPUT_CLASS}
              >
                <option value="">— выберите —</option>
                {members.map(member => (
                  <option key={member.person_id} value={member.person_id}>
                    {member.last_name} {member.first_name}
                  </option>
                ))}
              </select>
            </label>

            {previewLoading && (
              <div className="flex h-20 items-center justify-center gap-2 rounded border border-border bg-muted/30 text-sm text-muted-foreground">
                <Loader2 size={16} className="animate-spin" />
                Загрузка договоров
              </div>
            )}

            {preview && !previewLoading && (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="block text-xs font-medium text-muted-foreground">
                    Новое имя
                    <input value={newFirstName} onChange={event => setNewFirstName(event.target.value)} className={INPUT_CLASS} />
                  </label>
                  <label className="block text-xs font-medium text-muted-foreground">
                    Новая фамилия
                    <input value={newLastName} onChange={event => setNewLastName(event.target.value)} className={INPUT_CLASS} />
                  </label>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="block text-xs font-medium text-muted-foreground">
                    Дата подписания ДС
                    <input
                      type="date"
                      value={signedAt}
                      onChange={event => setSignedAt(event.target.value)}
                      className={INPUT_CLASS}
                    />
                  </label>
                  <label className="block text-xs font-medium text-muted-foreground">
                    Действует с
                    <input
                      type="date"
                      value={effectiveFrom}
                      onChange={event => setEffectiveFrom(event.target.value)}
                      className={INPUT_CLASS}
                    />
                  </label>
                </div>

                <div className="rounded-md border border-border p-3">
                  <div className="mb-2 text-xs font-medium text-foreground">Договоры</div>
                  {preview.contracts.length === 0 ? (
                    <div className="text-xs text-muted-foreground">Договоры не найдены.</div>
                  ) : (
                    <div className="space-y-2">
                      {preview.contracts.map(contract => (
                        <label key={contract.contract_id} className="flex items-start gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={Boolean(contractChecked[contract.contract_id])}
                            onChange={event => setContractChecked(previous => ({
                              ...previous,
                              [contract.contract_id]: event.target.checked,
                            }))}
                            className="mt-0.5"
                          />
                          <span>
                            <span className="text-foreground">
                              ДКК {contract.contract_number ?? `#${contract.contract_id}`}
                              {contract.is_active ? '' : ' · завершён'}
                            </span>
                            {contract.legal_name && (
                              <span className="mt-0.5 block text-muted-foreground">
                                {contract.legal_name}{contract.inn ? ` · ИНН ${contract.inn}` : ''}
                              </span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                  Имя изменится сразу после сохранения, даже если «Действует с» указано будущей датой.
                  Для выбранных договоров будут созданы ДС. Имя участника, связанного аккаунта
                  и подходящих ИП обновится автоматически. Отдельная «Форма имени в списках»
                  будет сброшена, чтобы везде сразу показывалось новое имя.
                </div>

                <div className="flex items-center justify-end gap-2">
                  <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Закрыть</Button>
                  <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
                    {saving && <Loader2 size={15} className="mr-2 animate-spin" />}
                    Сохранить и создать ДС
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </section>
    </div>
  )
}
